/**
 * SAP CATS bridge — types + pure helpers + mock provider.
 *
 * Mirrors the BAPI_CATIMESHEETMGR_INSERT / _CHANGE status flow:
 *   20 — Entered (timesheet exists in CATS, not yet released)
 *   30 — Released (supervisor approved; PS confirmation if applicable)
 *   60 — Transferred (cost flowed to CO/FI; CO-PA derivation captured)
 *
 * Mach12 submission state ↔ CATS status mapping:
 *   - Sign + submit  → posts to CATS as status 20
 *   - Approve        → CATS transitions to 30, then 60 once final tier
 *                       resolves (in the slim moonshot flow these collapse
 *                       to a single approve action that drives both)
 *   - Reject         → status 'rejected', record kept for audit
 *
 * Today the implementation is mock-only — `MockSapCatsProvider` writes
 * to in-memory state. Production landing path uses node-rfc BAPI
 * invocations from an api/ proxy + OData for S/4HANA Public Cloud.
 * The provider adapter shape stays stable; only the body changes.
 */

import type { TsContract, TsSubmission } from '../types/timesheet.types'
import type { CatsLineItemsPayload } from './catsLineItems'

export type SapCatsStatus =
  | 'not-posted'
  | 'entered-20'
  | 'released-30'
  | 'transferred-60'
  | 'rejected'

export interface SapCopaDerivation {
  costElement: string
  profitCenter: string
  profitabilitySegment: string
  companyCode: string
  /** PS network confirmation when the WBS has an underlying network activity. */
  cn25Confirmation?: string
  derivedAt: string
}

export interface SapCatsRecord {
  /** Mach12 submission id this CATS record mirrors. */
  submissionId: string
  /** SAP-side document number — 10-digit string. */
  catsDocumentNumber: string
  status: SapCatsStatus
  costCenter: string
  /** Network / activity (PS Project Systems) when the contract has WBS. */
  networkActivity?: string
  updatedAt: string
  /** Human-readable last action ("Posted at 14:33", etc.). */
  lastEvent: string
  /** Status-transition history. */
  history: Array<{ status: SapCatsStatus; at: string; note: string }>
  copaDerivation?: SapCopaDerivation
  /**
   * TS-7.1 — per-day CATSDB line-item payloads attached to this
   * submission. Each entry is the output of buildCatsLineItems for one
   * (worker × workdate) and represents the rows that would flow to
   * CATSDB on post. Caller attaches via provider.attachLineItems()
   * after the record is created. Reversal/replacement pairs (TS-11.9)
   * accumulate here so the audit trail of corrections is visible
   * inline with the original posting.
   */
  lineItemsPayloads?: CatsLineItemsPayload[]
}

// ─── Pure helpers ────────────────────────────────────────────────────────────

const COST_ELEMENT_BY_POOL: Record<string, string> = {
  default: '500100 — Direct labor',
  'G&A': '600200 — G&A pool',
  'B&P': '600300 — Bid & Proposal',
  'IR&D': '600400 — Independent R&D',
  Fringe: '600500 — Fringe pool',
  OH: '600100 — Overhead pool',
}

const DEFAULT_COST_ELEMENT = '500100 — Direct labor'

export function deriveCostElement(pool: string | null | undefined): string {
  if (!pool) return DEFAULT_COST_ELEMENT
  return COST_ELEMENT_BY_POOL[pool] ?? DEFAULT_COST_ELEMENT
}

export function deriveCostCenter(contract: TsContract): string {
  // Real impl reads from SAP HR cost-center master via BAPI. Mock derives
  // from contract code so demo data renders meaningfully.
  const prefix = (contract.code.replace(/[^A-Z0-9]/gi, '').slice(0, 5).toUpperCase()) || 'GEN'
  const suffix = (contract.id.replace(/[^A-Z0-9]/gi, '').slice(-4).toUpperCase()).padStart(4, '0')
  return `${prefix}-${suffix}`
}

export function deriveProfitabilitySegment(contract: TsContract): string {
  // CO-PA characteristic combination — mock uses contract type + agency.
  return `${contract.type}-${contract.agency.replace(/\s+/g, '_').toUpperCase().slice(0, 12)}`
}

export function genCatsDocumentNumber(seed?: string): string {
  // Real CATS docs are 10-digit ints assigned by SAP. Mock uses a seed-
  // stable hash so tests get deterministic numbers.
  if (seed) {
    let h = 0
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
    const n = Math.abs(h) % 9_999_999_999
    return String(n).padStart(10, '0')
  }
  return String(Math.floor(Math.random() * 9_000_000_000) + 1_000_000_000)
}

export function isTerminalStatus(status: SapCatsStatus): boolean {
  return status === 'transferred-60' || status === 'rejected'
}

export function nextStatusOnApprove(status: SapCatsStatus): SapCatsStatus {
  switch (status) {
    case 'not-posted': return 'entered-20'
    case 'entered-20': return 'released-30'
    case 'released-30': return 'transferred-60'
    default: return status  // already terminal
  }
}

// ─── Provider adapter (mock-only today) ─────────────────────────────────────

export interface SapCatsProvider {
  name: string
  /** Post a submission to CATS at status 'entered-20'. */
  postSubmission(submission: TsSubmission, contract: TsContract): Promise<SapCatsRecord>
  /** Advance an existing CATS record to the next status (20 → 30 → 60). */
  advance(submissionId: string, note?: string): Promise<SapCatsRecord | null>
  /** Mark the CATS record as rejected. */
  reject(submissionId: string, reason: string): Promise<SapCatsRecord | null>
  /** Read the CATS record for a submission, or null if never posted. */
  getRecord(submissionId: string): Promise<SapCatsRecord | null>
  /** Cumulative — counts of records in each status across the whole bridge. */
  getStatusCounts(): Promise<Record<SapCatsStatus, number>>
  /**
   * TS-7.1 — attach a CATSDB line-items payload (built via
   * buildCatsLineItems) to an existing record. Append-only — multiple
   * payloads stack (e.g. one per workday in the submission, plus any
   * reversal/replacement pairs from TS-11.9 corrections). Returns
   * the updated record, or null when the submissionId is not found.
   */
  attachLineItems(
    submissionId: string,
    payload: CatsLineItemsPayload,
  ): Promise<SapCatsRecord | null>
}

class MockSapCatsProvider implements SapCatsProvider {
  name = 'mock'
  private store = new Map<string, SapCatsRecord>()

  async postSubmission(submission: TsSubmission, contract: TsContract): Promise<SapCatsRecord> {
    const now = new Date().toISOString()
    const record: SapCatsRecord = {
      submissionId: submission.id,
      catsDocumentNumber: genCatsDocumentNumber(submission.id),
      status: 'entered-20',
      costCenter: deriveCostCenter(contract),
      updatedAt: now,
      lastEvent: `Posted to CATS at ${now.slice(11, 16)} UTC`,
      history: [{ status: 'entered-20', at: now, note: `Posted from Mach12 submission ${submission.id}` }],
    }
    this.store.set(submission.id, record)
    return record
  }

  async advance(submissionId: string, note?: string): Promise<SapCatsRecord | null> {
    const record = this.store.get(submissionId)
    if (!record) return null
    if (isTerminalStatus(record.status)) return record
    const now = new Date().toISOString()
    const nextStatus = nextStatusOnApprove(record.status)
    record.status = nextStatus
    record.updatedAt = now
    record.lastEvent = note ?? `Advanced to ${nextStatus} at ${now.slice(11, 16)} UTC`
    record.history.push({ status: nextStatus, at: now, note: note ?? 'Advanced via Mach12 approval' })
    if (nextStatus === 'transferred-60') {
      record.copaDerivation = {
        costElement: DEFAULT_COST_ELEMENT,
        profitCenter: 'PC-DEMO',
        profitabilitySegment: 'PA-DEMO',
        companyCode: '1000',
        derivedAt: now,
      }
    }
    return record
  }

  async reject(submissionId: string, reason: string): Promise<SapCatsRecord | null> {
    const record = this.store.get(submissionId)
    if (!record) return null
    const now = new Date().toISOString()
    record.status = 'rejected'
    record.updatedAt = now
    record.lastEvent = `Rejected at ${now.slice(11, 16)} UTC: ${reason}`
    record.history.push({ status: 'rejected', at: now, note: reason })
    return record
  }

  async getRecord(submissionId: string): Promise<SapCatsRecord | null> {
    return this.store.get(submissionId) ?? null
  }

  async getStatusCounts(): Promise<Record<SapCatsStatus, number>> {
    const counts: Record<SapCatsStatus, number> = {
      'not-posted': 0,
      'entered-20': 0,
      'released-30': 0,
      'transferred-60': 0,
      rejected: 0,
    }
    for (const r of this.store.values()) {
      counts[r.status] = (counts[r.status] ?? 0) + 1
    }
    return counts
  }

  async attachLineItems(
    submissionId: string,
    payload: CatsLineItemsPayload,
  ): Promise<SapCatsRecord | null> {
    const record = this.store.get(submissionId)
    if (!record) return null
    const now = new Date().toISOString()
    record.lineItemsPayloads = [...(record.lineItemsPayloads ?? []), payload]
    record.updatedAt = now
    // Compact event note: distinguish reversal/replacement from forward post
    const kind = payload.reversalOf
      ? 'reversal'
      : payload.replacementFor
        ? 'replacement'
        : 'lineitems'
    record.lastEvent = `Attached ${kind} payload (${payload.lines.length} lines · ${payload.totalHours}h) at ${now.slice(11, 16)} UTC`
    record.history.push({
      status: record.status,
      at: now,
      note: payload.reversalOf
        ? `Reversal of ${payload.reversalOf}${payload.correctionReason ? ` — ${payload.correctionReason}` : ''}`
        : payload.replacementFor
          ? `Replacement for ${payload.replacementFor}${payload.correctionReason ? ` — ${payload.correctionReason}` : ''}`
          : `CATSDB line items for ${payload.workdate} (pernr ${payload.pernr})`,
    })
    return record
  }

  /** Test-only — reset between runs. */
  reset(): void { this.store.clear() }
}

let provider: SapCatsProvider = new MockSapCatsProvider()

export function getSapCatsProvider(): SapCatsProvider {
  return provider
}

/** Test-hook — swap in a different provider. */
export function setSapCatsProvider(p: SapCatsProvider): void {
  provider = p
}

/** Test-hook — wipe the mock provider's in-memory store. */
export function _resetMockSapCats(): void {
  if (provider instanceof MockSapCatsProvider) provider.reset()
}
